import { Typography, Card, Row, Col, Statistic, Skeleton, Button, Empty } from 'antd';
import { TeamOutlined, UserOutlined, BookOutlined, RiseOutlined, BarChartOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { api } from '@/services/api';
import { Overview } from '@/types';

interface InteractionDay { date: string; count: number }
interface TopDeck { deckId: string; name: string; assigned: number; completed: number; completion: number }
interface StrugglingCard { cardId: string; name: string; notUnderstood: number }

const DECK_COLORS = ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];
const CARD_COLORS = ['#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2', '#dc2626', '#f43f5e', '#fb7185', '#fda4af', '#fecdd3'];

function truncateLabel(label: string, maxLen = 18): string {
  return label.length > maxLen ? label.slice(0, maxLen - 1) + '…' : label;
}

export default function AnalyticsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.get('/admin/analytics/overview'),
  });

  const { data: interactionsData } = useQuery({
    queryKey: ['admin-interactions-daily'],
    queryFn: () => api.get<InteractionDay[]>('/admin/analytics/interactions-daily'),
  });

  const { data: topDecksData } = useQuery({
    queryKey: ['admin-top-decks'],
    queryFn: () => api.get<TopDeck[]>('/admin/analytics/top-decks'),
  });

  const { data: strugglingData } = useQuery({
    queryKey: ['admin-struggling-cards'],
    queryFn: () => api.get<StrugglingCard[]>('/admin/analytics/struggling-cards'),
  });

  const stats: Overview | undefined = (data as { data: Overview })?.data;
  const interactionsRaw = interactionsData as unknown as { data: InteractionDay[] } | InteractionDay[] | undefined;
  const interactions: InteractionDay[] = Array.isArray(interactionsRaw) ? interactionsRaw : (interactionsRaw?.data ?? []);
  const topDecksRaw = topDecksData as unknown as { data: TopDeck[] } | TopDeck[] | undefined;
  const topDecks: TopDeck[] = Array.isArray(topDecksRaw) ? topDecksRaw : (topDecksRaw?.data ?? []);
  const strugglingRaw = strugglingData as unknown as { data: StrugglingCard[] } | StrugglingCard[] | undefined;
  const struggling: StrugglingCard[] = Array.isArray(strugglingRaw) ? strugglingRaw : (strugglingRaw?.data ?? []);

  if (isError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, flexDirection: 'column' }}>
        <Typography.Text style={{ color: '#ef4444', marginBottom: 16 }}>Failed to load analytics data</Typography.Text>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  const statCards = [
    { title: 'Total Users', value: (stats?.totalTeachers ?? 0) + (stats?.totalStudents ?? 0), icon: <TeamOutlined />, color: '#4f46e5' },
    { title: 'Teachers', value: stats?.totalTeachers ?? 0, icon: <UserOutlined />, color: '#059669' },
    { title: 'Students', value: stats?.totalStudents ?? 0, icon: <UserOutlined />, color: '#d97706' },
    { title: 'Decks', value: stats?.totalDecks ?? 0, icon: <BookOutlined />, color: '#0891b2' },
    { title: 'DAU', value: stats?.dau ?? 0, icon: <RiseOutlined />, color: '#7c3aed' },
    { title: 'MAU', value: stats?.mau ?? 0, icon: <BarChartOutlined />, color: '#dc2626' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Analytics</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Platform-wide usage analytics and trends</Typography.Text>
      </div>

      {/* Stat cards */}
      {isLoading ? <Skeleton active /> : (
        <Row gutter={[16, 16]}>
          {statCards.map((s) => (
            <Col xs={12} sm={8} lg={4} key={s.title}>
              <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
                <Statistic
                  title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>{s.title}</span>}
                  value={s.value}
                  prefix={<span style={{ color: s.color }}>{s.icon}</span>}
                  valueStyle={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Interactions line chart */}
      <Card
        title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Interactions Over Time (Last 30 Days)</span>}
        style={{ border: '1px solid #e2e8f0', borderRadius: 12, marginTop: 16 }}
        styles={{ header: { borderBottom: '1px solid #f1f5f9', padding: '12px 20px', minHeight: 'auto' } }}
      >
        {interactions.length === 0 ? (
          <Empty description="No interaction data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={interactions} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={(v: string) => v.slice(5)}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip
                labelFormatter={(label) => `Date: ${label}`}
                formatter={(value: number) => [value.toLocaleString(), 'Interactions']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#4f46e5"
                strokeWidth={2}
                dot={{ r: 3, fill: '#4f46e5' }}
                activeDot={{ r: 5, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Bar charts */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Top Decks by Completion %</span>}
            style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}
            styles={{ header: { borderBottom: '1px solid #f1f5f9', padding: '12px 20px', minHeight: 'auto' } }}
          >
            {topDecks.length === 0 ? (
              <Empty description="No deck data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(250, topDecks.length * 44)}>
                <BarChart data={topDecks} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 12, fill: '#475569' }}
                    tickFormatter={(v) => truncateLabel(v)}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value}%`, 'Completion']}
                    labelFormatter={(label) => `Deck: ${label}`}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                  />
                  <Bar dataKey="completion" radius={[0, 6, 6, 0]} barSize={24}>
                    {topDecks.map((_entry, idx) => (
                      <Cell key={idx} fill={DECK_COLORS[idx % DECK_COLORS.length]} />
                    ))}
                    <LabelList dataKey="completion" position="right" formatter={(v: number) => `${v}%`} style={{ fontSize: 11, fontWeight: 600, fill: '#475569' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Cards with Most "Not Understood"</span>}
            style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}
            styles={{ header: { borderBottom: '1px solid #f1f5f9', padding: '12px 20px', minHeight: 'auto' } }}
          >
            {struggling.length === 0 ? (
              <Empty description="No interaction data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(250, struggling.length * 44)}>
                <BarChart data={struggling} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 12, fill: '#475569' }}
                    tickFormatter={(v) => truncateLabel(v)}
                  />
                  <Tooltip
                    formatter={(value: number) => [value, 'Not Understood']}
                    labelFormatter={(label) => `Card: ${label}`}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                  />
                  <Bar dataKey="notUnderstood" radius={[0, 6, 6, 0]} barSize={24}>
                    {struggling.map((_entry, idx) => (
                      <Cell key={idx} fill={CARD_COLORS[idx % CARD_COLORS.length]} />
                    ))}
                    <LabelList dataKey="notUnderstood" position="right" style={{ fontSize: 11, fontWeight: 600, fill: '#475569' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
